<template>
    <Dialog v-model:open="opened">
        <DialogTrigger>
            <slot />
        </DialogTrigger>
        <DialogContent class="max-w-5xl">
            <DialogHeader>
                <DialogTitle>
                    {{
                        $t("integrations.stripe.modal.example_invoice_title")
                    }}</DialogTitle
                >
            </DialogHeader>

            <div v-if="stripeConfigLoading" class="h-[450px]"></div>

            <div v-if="invoiceData && !stripeConfigLoading">
                <h1 class="text-2xl font-medium">Faktura</h1>

                <div class="mt-4">
                    <div class="grid grid-cols-[200px_1fr]">
                        <p class="font-medium">Číslo faktury</p>
                        <p>123456789</p>
                    </div>
                    <div class="grid grid-cols-[200px_1fr]">
                        <p class="font-medium">Datum vystavení</p>
                        <p>1. 1. 2024</p>
                    </div>
                    <div class="grid grid-cols-[200px_1fr]">
                        <p class="font-medium">Datum splatnosti</p>
                        <p>1. 1. 2024</p>
                    </div>

                    <div v-if="crn" class="grid grid-cols-[200px_1fr]">
                        <p class="font-medium">Dodavatel IČO</p>
                        <p>{{ crn }}</p>
                    </div>
                </div>

                <div class="mt-12 grid grid-cols-2">
                    <div>
                        <p class="font-medium">
                            {{
                                invoiceData.name ||
                                "Název společnosti (nevyplněno)"
                            }}
                        </p>
                        <p>
                            {{
                                invoiceData.street ||
                                "Ulice a číslo popisné (nevyplněno)"
                            }}
                        </p>
                        <p>
                            {{ invoiceData.postal_code || "PSČ (nevyplněno)" }}
                            {{ invoiceData.city || "Město (nevyplněno)" }}
                        </p>
                        <p>{{ invoiceData.country || "Země (nevyplněna)" }}</p>
                        <p>{{ invoiceData.email || "E-mail (nevyplněn)" }}</p>
                        <p v-if="taxId">
                            {{ invoiceData.country }} VAT {{ taxId }}
                        </p>
                    </div>
                    <div>
                        <p class="font-medium">Odběratel</p>
                        <p>Karel Novák</p>
                        <p>Na výsluní 1010</p>
                        <p>11111 Praha</p>
                        <p>Czech Republic</p>
                        <p>email-zakaznika@example.com</p>
                    </div>
                </div>

                <div class="mt-16">
                    <div
                        class="gap-3 grid"
                        :class="[
                            isTaxPayer
                                ? 'grid-cols-[1fr_100px_150px_100px_100px]'
                                : 'grid-cols-[1fr_100px_150px_100px]',
                        ]"
                    >
                        <div>Popis</div>
                        <div class="justify-self-end">Množství</div>
                        <div class="justify-self-end">Jednotková cena</div>
                        <div v-if="isTaxPayer" class="justify-self-end">
                            Daň
                        </div>
                        <div class="justify-self-end">Částka</div>
                    </div>

                    <hr class="my-4" />
                    <div
                        class="gap-3 grid"
                        :class="[
                            isTaxPayer
                                ? 'grid-cols-[1fr_100px_150px_100px_100px]'
                                : 'grid-cols-[1fr_100px_150px_100px]',
                        ]"
                    >
                        <div>Položka faktury (např. konzultace)</div>
                        <div class="justify-self-end">1</div>
                        <div class="justify-self-end">100,00 Kč</div>
                        <div v-if="isTaxPayer" class="justify-self-end">
                            21 %
                        </div>
                        <div class="justify-self-end">100,00 Kč</div>
                    </div>
                </div>

                <div class="mt-12 grid grid-cols-2">
                    <div></div>
                    <div class="w-full justify-self-end">
                        <hr />
                        <div class="my-2 grid grid-cols-[1fr_120px]">
                            <p>Mezi součet</p>
                            <p class="justify-self-end">100,00Kč</p>
                        </div>
                        <hr v-if="isTaxPayer" />
                        <div
                            v-if="isTaxPayer"
                            class="my-2 grid grid-cols-[1fr_120px]"
                        >
                            <p>Součet bez daně</p>
                            <p class="justify-self-end">100,00Kč</p>
                        </div>
                        <hr v-if="isTaxPayer" />
                        <div
                            v-if="isTaxPayer"
                            class="my-2 grid grid-cols-[1fr_120px]"
                        >
                            <p>VAT - Czech Republic (21 % z 100,00 Kč)</p>
                            <p class="justify-self-end">100,00Kč</p>
                        </div>
                        <hr />
                        <div class="my-2 grid grid-cols-[1fr_120px]">
                            <p>Celkem</p>
                            <p class="justify-self-end">121,00Kč</p>
                        </div>
                        <hr />
                        <div
                            class="my-2 font-medium grid grid-cols-[1fr_120px]"
                        >
                            <p>Splatná částka</p>
                            <p class="justify-self-end">121,00Kč</p>
                        </div>
                    </div>
                </div>
            </div>

            <DialogFooter>
                <DialogClose>
                    <Button>{{ $t("close") }}</Button>
                </DialogClose>
            </DialogFooter>
        </DialogContent>
    </Dialog>
</template>

<script lang="ts" setup>
import { Button } from "@/components/ui/button";
import { ref, watch } from "vue";
import axios from "axios";

type InvoiceData = {
    name: string | null;
    country: string | null;
    city: string | null;
    street: string | null;
    postal_code: string | null;
    email: string | null;
    phone: string | null;
    url: string | null;
};

import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    DialogClose,
} from "@/components/ui/dialog";

const props = defineProps<{
    crn?: string;
    taxId?: string;
    isTaxPayer?: boolean;
    integrationId: number;
}>();

const opened = ref(false);
const stripeConfigLoading = ref(true);
const invoiceData = ref<InvoiceData>();

watch(opened, (value) => {
    if (value === true) {
        getStripeStatusForIntegration();
    }
});

async function getStripeStatusForIntegration() {
    try {
        const res = await axios.get(
            `/stripe-integrations/${props.integrationId}/missing-config`,
        );

        invoiceData.value = res.data.data.invoiceData;
    } catch (err) {
        console.error(err);
    } finally {
        stripeConfigLoading.value = false;
    }
}
</script>
